import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { FONT_FAMILY } from "../../common/sharedFunctions";
import { useTranslation } from "react-i18next";
import { 
  Home, LayoutDashboard, List, PlusCircle, Users, Car, Truck,
  Wallet, FileText, Bell, Shield, AlertCircle, CreditCard, Settings, 
  User, LogOut, X, ChevronRight, Tag, Award, MapPin, Trophy, ClipboardList
} from 'lucide-react';

const menuSections = [
  {
    label: 'Main',
    items: [
      { name: 'home', url: '/', icon: Home, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
      { name: 'dashboard_text', url: '/dashboard', icon: LayoutDashboard, access: ['admin', 'fleetadmin'] },
    ]
  },
  {
    label: 'Bookings',
    items: [
      { name: 'booking_history', url: '/bookings', icon: List, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
      { name: 'addbookinglable', url: '/addbookings', icon: PlusCircle, access: ['admin', 'fleetadmin', 'customer'] },
      { name: 'Booking Queue', url: '/booking-queue', icon: ClipboardList, access: ['admin', 'fleetadmin'] },
    ]
  },
  {
    label: 'Management',
    items: [
      { name: 'user', url: '/users/0', icon: Users, access: ['admin', 'fleetadmin'] },
      { name: 'car_type', url: '/cartypes', icon: Truck, access: ['admin'] },
      { name: 'cars', url: '/cars', icon: Car, access: ['admin', 'fleetadmin', 'driver'] },
      { name: 'driver_rewards_league', url: '/driverrewards', icon: Trophy, access: ['admin'] },
      { name: 'realtime_drivers', url: '/drivermap', icon: MapPin, access: ['admin', 'fleetadmin'] },
      { name: 'promo', url: '/promos', icon: Tag, access: ['admin'] },
    ]
  },
  {
    label: 'Finance',
    items: [
      { name: 'my_wallet_tile', url: '/userwallet', icon: Wallet, access: ['driver', 'customer', 'fleetadmin'] },
      { name: 'withdraws_web', url: '/withdraws', icon: CreditCard, access: ['admin'] },
      { name: 'add_to_wallet', url: '/addtowallet', icon: Wallet, access: ['admin'] },
      { name: 'report', url: '/allreports', icon: FileText, access: ['admin', 'fleetadmin'] },
    ]
  },
  {
    label: 'Communication',
    items: [
      { name: 'push_notifications', url: '/notifications', icon: Bell, access: ['admin'] },
      { name: 'sos', url: '/sos', icon: Shield, access: ['admin'] },
      { name: 'complain', url: '/complain', icon: AlertCircle, access: ['admin'] },
    ]
  },
  {
    label: 'Settings',
    items: [
      { name: 'settings_title', url: '/settings', icon: Settings, access: ['admin'] },
      { name: 'league_settings', url: '/leaguesettings', icon: Trophy, access: ['admin'] },
      { name: 'payment_settings', url: '/paymentsettings', icon: CreditCard, access: ['admin'] },
      { name: 'profile', url: '/profile', icon: User, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
    ]
  }
];

const SidebarContent = ({ role, onClose, navigate, t, isRTL, location, settings, handleLogout }) => (
  <>
    {/* Logo */}
    <div className="h-16 flex items-center justify-between px-5 border-b border-white/5 shrink-0">
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => { navigate('/'); if (onClose) onClose(); }}>
        <img src="/gruwcer-logo.png" alt="Gruwcer Delivery" className="h-8 w-auto object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
      </div>
      {onClose && (
        <button onClick={onClose} className="lg:hidden p-2 hover:bg-white/10 rounded-2xl transition-colors">
          <X className="w-5 h-5 text-white/70" />
        </button>
      )}
    </div>

    {/* Navigation */}
    <nav className="px-3 py-4 overflow-y-auto flex-1">
      {menuSections.map((section, sIdx) => {
        const sectionItems = section.items.filter(item => item.access.includes(role));
        if (sectionItems.length === 0) return null;
        return (
          <div key={sIdx} className="mb-4">
            <p className="px-4 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {sectionItems.map((item) => {
                const isActive = location.pathname === item.url;
                return (
                  <Link
                    key={item.url}
                    to={item.url}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-2xl transition-all duration-200 group ${
                      isActive
                        ? 'bg-gradient-to-r from-[#009689]/20 to-[#319F44]/20 text-white border border-[#009689]/30'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <item.icon className={`w-[18px] h-[18px] transition-all ${isActive ? 'text-[#009689]' : 'group-hover:text-[#009689]'}`} />
                    <span className="font-medium text-[13px]" style={{ fontFamily: FONT_FAMILY }}>{t(item.name)}</span>
                    {isActive && <ChevronRight className="w-3.5 h-3.5 ml-auto text-[#009689]" />}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>

    {/* Logout */}
    <div className="p-3 border-t border-white/5 shrink-0">
      <button
        onClick={handleLogout}
        className="flex items-center gap-3 px-4 py-2.5 w-full rounded-2xl text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
      >
        <LogOut className="w-[18px] h-[18px]" />
        <span className="font-medium text-[13px]" style={{ fontFamily: FONT_FAMILY }}>{t('logout')}</span>
      </button>
    </div>
  </>
);

const ModernSidebar = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { signOff } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);

  const [role, setRole] = useState(null);

  useEffect(() => {
    if (auth.profile?.usertype) setRole(auth.profile.usertype);
  }, [auth.profile]);

  const handleLogout = () => dispatch(signOff());

  const contentProps = { role, onClose, navigate, t, isRTL, location, settings, handleLogout };

  return (
    <>
      {/* Desktop sidebar - always visible */}
      <div
        className="hidden lg:flex lg:flex-col lg:w-[280px] lg:shrink-0 h-full bg-gradient-to-b from-gray-900 to-gray-800"
        style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
      >
        <SidebarContent {...contentProps} onClose={null} />
      </div>

      {/* Mobile overlay */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Mobile sidebar - animated */}
      <motion.aside
        initial={false}
        animate={{ x: isOpen ? 0 : (isRTL === 'rtl' ? 280 : -280) }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`fixed ${isRTL === 'rtl' ? 'right-0' : 'left-0'} top-0 z-50 h-full w-[280px] shadow-2xl lg:hidden flex flex-col bg-gradient-to-b from-gray-900 to-gray-800`}
        style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
      >
        <SidebarContent {...contentProps} />
      </motion.aside>
    </>
  );
};

export default ModernSidebar;
